import React from "react";
import PropTypes from "prop-types";

import { formatShortDate } from "../chiefComplaints/dateHelpers";
import { TREND_COLOR, TrendDot, TrendTip, useTrendTip } from "../trendChart";

/**
 * "Treatment status over time" chart for the Diagnosis trend modal.
 *
 * Shape mirrors CC v2's StatusOverTimeChart step function but collapses the
 * VAS 0–10 scale into two fixed lanes:
 *
 *   Ongoing   ──────────●──────────●          ●──────────
 *                                  │          │
 *   Resolved                       ●──────────●
 *
 * A horizontal segment sits at each entry's Y (Ongoing or Resolved); a
 * vertical connector jumps at each transition. Dots mark every entry's
 * start so the reader can count episodes at a glance.
 *
 * Delete events are ignored — they don't change treatment status. The audit
 * table below the chart still shows the delete row.
 */
const W = 680;
const H = 220;
const PAD = { l: 80, r: 16, t: 30, b: 34 };
const IW = W - PAD.l - PAD.r;
const IH = H - PAD.t - PAD.b;

// Two lanes: Ongoing sits at 25% down, Resolved at 75%.
const laneY = (ongoing) => PAD.t + (ongoing ? 0.2 : 0.75) * IH;


// Board 4090:15075: every trend chart draws in #6a5ef5.
const LINE_COLOR = TREND_COLOR;
const ONGOING_COLOR = "#c2410c"; // orange for "Ongoing" label
const RESOLVED_COLOR = "#3b6fb5"; // blue for "Resolved" label

const TreatmentStatusChart = ({ audit }) => {
  // White HTML tooltip in place of the native SVG <title>. Wrapper needs
  // position: relative (set on `.cc-chart` in the shared SCSS).
  const { wrapRef, tip, showTip, hideTip } = useTrendTip();

  const entries = (audit || [])
    .filter((e) => e && e.event !== "delete" && e.sinceWhen)
    .map((e) => ({ ...e, _t: new Date(e.sinceWhen).getTime() }))
    .filter((e) => !isNaN(e._t))
    .sort((a, b) => a._t - b._t);

  if (entries.length === 0) {
    return <div className="cc-chart-empty">No status entries to chart yet.</div>;
  }

  const now = Date.now();
  const tMin = entries[0]._t;
  const lastEndT = entries[entries.length - 1].toWhen
    ? new Date(entries[entries.length - 1].toWhen).getTime()
    : now;
  // Guarantee a non-zero range so timeToX is well-defined for single-point audits.
  const tMax = Math.max(lastEndT, tMin + 24 * 60 * 60 * 1000);

  const timeToX = (t) =>
    tMax === tMin ? PAD.l + IW / 2 : PAD.l + ((t - tMin) / (tMax - tMin)) * IW;

  // Build the step-function path. Each entry contributes a horizontal segment
  // at its lane; between entries a vertical connector jumps to the next lane
  // at the changeover time.
  let d = "";
  entries.forEach((e, i) => {
    const startT = e._t;
    const endT = e.toWhen
      ? new Date(e.toWhen).getTime()
      : i === entries.length - 1
      ? now
      : entries[i + 1]._t;
    const x1 = timeToX(startT);
    const x2 = timeToX(endT);
    const y = laneY(e.ongoing);

    if (i === 0) {
      d += `M ${x1} ${y} L ${x2} ${y}`;
    } else {
      const prevY = laneY(entries[i - 1].ongoing);
      d += ` L ${x1} ${prevY} L ${x1} ${y} L ${x2} ${y}`;
    }
  });

  // A handful of X-axis date ticks — 4 evenly-spaced tick marks including
  // the two endpoints. Keeps the timeline readable without an axis library.
  const ticks = [0, 0.33, 0.66, 1].map((f) => tMin + f * (tMax - tMin));

  return (
    <div className="cc-chart" ref={wrapRef}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        preserveAspectRatio="none"
        role="img"
        aria-label="Treatment status over time"
      >
        {/* Lane gridlines — very light so the step function reads first. */}
        <line x1={PAD.l} x2={PAD.l + IW} y1={laneY(true)} y2={laneY(true)} stroke="#eef0f4" />
        <line x1={PAD.l} x2={PAD.l + IW} y1={laneY(false)} y2={laneY(false)} stroke="#eef0f4" />

        {/* Y-axis labels — matches the mock colours. */}
        <text
          x={PAD.l - 12}
          y={laneY(true) + 4}
          textAnchor="end"
          fontSize={12}
          fontWeight={600}
          fill={ONGOING_COLOR}
        >
          Ongoing
        </text>
        <text
          x={PAD.l - 12}
          y={laneY(false) + 4}
          textAnchor="end"
          fontSize={12}
          fontWeight={600}
          fill={RESOLVED_COLOR}
        >
          Resolved
        </text>

        {/* Step function line */}
        <path d={d} stroke={LINE_COLOR} strokeWidth={2} fill="none" strokeLinejoin="miter" />

        {/* Dots at each entry's start — white HTML tooltip on hover (via
            useTrendTip) so the user can read the exact date + status
            without leaving the chart. Native SVG <title> dropped so we
            control the pill's appearance across OS/browsers. */}
        {entries.map((e, i) => {
          const text = `${formatShortDate(new Date(e._t).toISOString())} · ${e.ongoing ? "Ongoing" : "Resolved"}`;
          return (
            <TrendDot
              key={i}
              cx={timeToX(e._t)}
              cy={laneY(e.ongoing)}
              scale={0.8}
              onMouseEnter={(ev) => showTip(ev, text)}
              onMouseMove={(ev) => showTip(ev, text)}
              onMouseLeave={hideTip}
            />
          );
        })}

        {/* X-axis: four date ticks along the bottom. */}
        {ticks.map((t, i) => (
          <text
            key={i}
            x={timeToX(t)}
            y={H - 10}
            textAnchor={i === 0 ? "start" : i === ticks.length - 1 ? "end" : "middle"}
            fontSize={11}
            fill="#6b7280"
          >
            {formatShortDate(new Date(t).toISOString())}
          </text>
        ))}
      </svg>
      <TrendTip tip={tip} />
    </div>
  );
};

TreatmentStatusChart.propTypes = {
  audit: PropTypes.array,
};

export default TreatmentStatusChart;
